import {
  compact_dependency_text,
  find_step_number_by_section_id,
  clamp_step_number
} from './queue_form_method_shared';
import { get_visible_questions, question_input_type } from './queue_form_helpers';
import {
  build_file_answer_payload,
  build_standard_answers,
  is_question_answered as is_question_answered_helper,
  normalize_answer_value as normalize_answer_value_helper,
  uses_checkbox,
  uses_checkbox_group,
  uses_facial_verification,
  uses_file,
  uses_radio,
  uses_select,
  uses_textarea
} from './queue_form_helpers';
import {
  normalize_text,
  read_safe_object_value
} from '@/services/onboarding_form_helpers';

export const queueFormNavigationMethods = {
  go_to_previous_step() {
    if (this.current_step > 1) {
      this.current_step -= 1;
      this.$nextTick(() => this.scroll_active_step_into_view());
    }
  },
  async submit_or_continue() {
    if (!this.validate_current_step()) return;
    const current_step_index = this.current_step - 1;
    const next_section = this.intake_steps[current_step_index + 1] || null;
    const current_section = this.current_main_section || Reflect.get(this.intake_steps, current_step_index)|| null;
    const target_step = this.is_last_step
      ? this.current_step
      : clamp_step_number(this.current_step + 1, this.intake_steps.length);
    const target_section_id = this.is_last_step
      ? current_section?.id || null
      : next_section?.id || null;
    const saved_successfully = await this.submit_selected_application_form({
      preserve_current_step: true,
      target_step,
      target_section_id
    });
    if (saved_successfully && target_step > this.current_step) {
      const restored_step_from_section = find_step_number_by_section_id(this.intake_steps, target_section_id);
      this.current_step = clamp_step_number(restored_step_from_section || target_step, this.intake_steps.length);
      this.$nextTick(() => this.scroll_active_step_into_view());
    }
  },
  question_input_type,
  uses_textarea,
  uses_select,
  uses_radio,
  uses_checkbox_group,
  uses_facial_verification,
  uses_file,
  uses_checkbox,
  get_question_value(question) {
    if (this.uses_checkbox_group(question)) {
      const value = read_safe_object_value(this.form_values, question.id, []);
      return Array.isArray(value) ? value : [];
    }
    if (this.uses_checkbox(question)) {
      return Boolean(read_safe_object_value(this.form_values, question.id, false));
    }
    return read_safe_object_value(this.form_values, question.id, '');
  },
  should_show_sub_section_title(sub_section) {
    const sub_section_label = normalize_text(sub_section?.label);
    const main_section_label = normalize_text(this.current_main_section?.label);
    const total_sub_sections = this.current_main_section?.sub_sections?.length || 0;
    if (!sub_section_label) return false;
    if (total_sub_sections === 1 && sub_section_label === main_section_label) return false;
    return sub_section_label !== 'details';
  },
  get_current_step_questions() {
    if (!this.current_main_section) return [];
    return this.current_main_section.sub_sections.flatMap((sub_section) => this.visible_questions_for_sub_section(sub_section));
  },
  visible_questions_for_sub_section(sub_section) {
    return get_visible_questions(sub_section?.questions || [], this.form_values);
  },
  is_dependent_question(question) {
    return Boolean(question?.is_dependent_question);
  },
  dependent_question_hint(question) {
    if (!this.is_dependent_question(question)) return '';
    const parent_label = compact_dependency_text(question?.parent_question_label || 'the previous answer');
    const trigger = compact_dependency_text(question?.trigger_condition);
    return trigger
      ? `Shown when ${parent_label} is "${trigger}".`
      : `Shown based on ${parent_label}.`;
  },
  is_question_answered(question) {
    if (this.uses_facial_verification(question)) {
      const value = this.get_facial_verification_value(question.id);
      return Boolean(value?.completed);
    }
    return is_question_answered_helper(
      question,
      this.form_values,
      this.file_values,
      this.uses_file,
      this.uses_checkbox_group,
      read_safe_object_value
    );
  },
  validate_current_step() {
    const missing_question = this.get_current_step_questions().find((question) => (
      question.is_mandatory && !this.is_question_answered(question)
    ));
    if (missing_question) {
      this.form_page_error = `Complete ${missing_question.label} before continuing.`;
      return false;
    }
    this.form_page_error = '';
    return true;
  },
  normalize_answer_value(question, value) {
    return normalize_answer_value_helper(question, value, this.uses_checkbox_group, this.uses_checkbox);
  },
  buildStandardAnswers() {
    return build_standard_answers(
      this.intake_steps,
      (question) => this.uses_file(question),
      (question, value) => this.normalize_answer_value(question, value),
      this.form_values,
      (question) => this.uses_facial_verification(question)
    );
  },
  buildFileAnswerPayload() {
    return build_file_answer_payload(
      this.intake_steps,
      (question) => this.uses_file(question),
      this.file_values,
      this.form_values,
      (question) => this.uses_facial_verification(question)
    );
  }
};
